import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Package, ShoppingCart, Store, Settings, User, LogOut, Palette, Menu, X, ChevronRight, Image, Share2, Info, ImageIcon, FileText, Link as LinkIcon, ShieldAlert, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, firebaseUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Sadələşdirilmiş menyu - qruplar ilə
  const menuGroups = [
    {
      title: null, // Əsas
      items: [
        { icon: Home, label: '🏠 Ana Səhifə', path: '/dashboard', description: 'Statistika və ümumi baxış' },
      ]
    },
    {
      title: '🏪 Mağazam',
      items: [
        { icon: Store, label: 'Mağaza Adı & WhatsApp', path: '/dashboard/store/info', description: 'Ad və əlaqə' },
        { icon: ImageIcon, label: 'Logo', path: '/dashboard/store/logo', description: 'Mağaza şəkli' },
        { icon: FileText, label: 'Haqqımızda', path: '/dashboard/store/about', description: 'Təsvir' },
        { icon: Image, label: 'Arxa Fon Şəkli', path: '/dashboard/store/banner', description: 'Cover foto' },
      ]
    },
    {
      title: '📦 Məhsullar',
      items: [
        { icon: Package, label: 'Məhsullarım', path: '/dashboard/products', description: 'Əlavə et, redaktə et' },
        { icon: ShoppingCart, label: 'Sifarişlər', path: '/dashboard/orders', description: 'Müştəri sifarişləri' },
      ]
    },
    {
      title: '⚙️ Digər',
      items: [
        { icon: LinkIcon, label: 'Mağaza Linki', path: '/dashboard/store/link', description: 'Paylaş' },
        { icon: Palette, label: 'Dizayn & Rəng', path: '/dashboard/store/theme', description: 'Görünüş' },
        { icon: Share2, label: 'Sosial Media', path: '/dashboard/store/social', description: 'İnsta, FB...' },
        { icon: ShieldAlert, label: 'İdarəetmə', path: '/dashboard/store/management', description: 'Açıq/Bağlı' },
      ]
    }
  ];
  
  const adminItems = user?.is_admin ? [
    { icon: User, label: '👑 Admin Panel', path: '/admin' }
  ] : [];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-gray-50" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-black border-b border-gray-800 flex items-center justify-between px-4 z-40">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 hover:bg-gray-800 rounded-lg text-white"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/dashboard" className="text-lg font-bold text-white">I Veb</Link>
        <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center text-black text-sm font-semibold">
          {user?.full_name?.[0]?.toUpperCase() || 'U'}
        </div>
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-72 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">İ</span>
            </div>
            <span className="text-xl font-bold text-gray-900">I Veb</span>
          </Link>
          <button
            onClick={closeSidebar}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {menuGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="mb-4">
              {group.title && (
                <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {group.title}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeSidebar}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-black text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{item.label}</div>
                      </div>
                      {isActive && <ChevronRight className="w-4 h-4" />}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
          
          {/* Admin Link */}
          {adminItems.length > 0 && (
            <div className="pt-2 border-t border-gray-100">
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={closeSidebar}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-orange-600 hover:bg-orange-50 transition-all mt-1"
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          )}
        </nav>

        {/* User Profile */}
        <div className="p-3 border-t border-gray-100">
          {/* Help Link */}
          <a
            href="https://wa.me/994557063190?text=Salam, kömək lazımdır"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 mb-2"
          >
            <HelpCircle className="w-5 h-5 text-gray-400" />
            <span>Kömək lazımdır?</span>
          </a>
          
          {/* User Info */}
          <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-xl mb-2">
            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {user?.full_name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          
          {/* Logout */}
          <Button
            variant="ghost"
            className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 text-sm rounded-xl"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Çıxış et
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-14 md:pt-0 bg-gray-50">
        <div className="p-4 md:p-8 max-w-6xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
